import BasicResource from '@/api/resource';
import tasks from '@/api/resources/tasks';

class AdvertisersResource extends BasicResource {
    constructor () {
        super('advertisers');
    }

    dict (params) {
        return this.client.request ({
            method: 'get',
            path: '/upyield/v1/dict/advertisers',
            params: { ...params },
        });
    }

    activate (advertiserId) {
        return this.client.request ({
            method: 'put',
            path: `/upyield/v1/advertisers/${advertiserId}/unpause`,
        });
    }

    pause (advertiserId) {
        return this.client.request ({
            method: 'put',
            path: `/upyield/v1/advertisers/${advertiserId}/pause`,
        });
    }

    bulkPause (payload) {
        return this.client.request ({
            method: 'put',
            path: '/upyield/v1/advertisers/bulk-pause',
            data: payload,
        });
    }

    bulkResume (payload) {
        return this.client.request ({
            method: 'put',
            path: '/upyield/v1/advertisers/bulk-unpause',
            data: payload,
        });
    }

    getSyncConfig (advertiserId) {
        return this.client.request ({
            method: 'get',
            path: `/upyield/v1/advertisers/${advertiserId}/sync`,
        });
    }

    addSyncConfig ({ advertiserId, data }) {
        return this.client.request ({
            method: 'post',
            path: `/upyield/v1/advertisers/${advertiserId}/sync`,
            data,
        });
    }

    updateSyncConfig ({ advertiserId, syncConfigId, data }) {
        return this.client.request({
            method: 'put',
            path: `/upyield/v1/advertisers/${advertiserId}/sync/${syncConfigId}/update`,
            data: data,
        });
    }

    createSyncTask ({ title, configuration_id, requestOptions = {} }) {
        return this.client.request({
            method: 'post',
            path: '/upyield/v1/tasks/external-offers',
            data: { name: title, type: 'external_import_offers', context: { configuration_id } },
        }, { ...requestOptions });
    }

    cancelSyncTask (title) {
        return this.client.request({
            method: 'post',
            path: '/upyield/v1/tasks/external-offers/cancel',
            data: { name: title, type: 'external_import_offers' },
        });
    }

    startAsyncOfferPreview (taskId) {
        return this.client.request({
            method: 'put',
            path: `/upyield/v1/tasks/${taskId}/external-offers/step`,
            data: { next_step: 'prepare' },
        });
    }

    prepareOfferSync ({ title, configuration_id, callbackOnProgress, requestOptions = { showErrorMessages: false } }) {
        const isTaskReady = (responseData) => responseData.status === 'prepared' || responseData.status === 'failure';

        return this.createSyncTask({ title, configuration_id, requestOptions })
            .then(({ data: { data: { id } } }) => this.startAsyncOfferPreview(id))
            .then(({ data: { data: { id } } }) => tasks.getTaskStatus({ taskId: id, callbackOnProgress, isTaskReady, requestOptions }));
    }

    previewReadyOffers ({ taskId, params }) {
        return this.client.request({
            method: 'get',
            path: `/upyield/v1/tasks/${taskId}/external-offers/results`,
            params: { status: 'ready', ...params },
        });
    }

    startAsyncOfferSync (taskId) {
        return this.client.request({
            method: 'put',
            path: `/upyield/v1/tasks/${taskId}/external-offers/step`,
            data: { next_step: 'accept' },
        });
    }

    previewImportedOffers ({ taskId, params }) {
        return this.client.request({
            method: 'get',
            path: `/upyield/v1/tasks/${taskId}/external-offers/results`,
            params: { status: ['imported', 'failure'], ...params },
        });
    }

    reportDownload (params) {
        return this.client.download({
            method: 'get',
            responseType: 'blob',
            path: '/upyield/v1/advertisers/download',
            params,
        });
    }

    pauseConfig ({ advertiserId, syncConfigId }) {
        return this.client.request({
            method: 'put',
            path: `/upyield/v1/advertisers/${advertiserId}/sync/${syncConfigId}/pause`,
        });
    }

    enableConfig ({ advertiserId, syncConfigId }) {
        return this.client.request({
            method: 'put',
            path: `/upyield/v1/advertisers/${advertiserId}/sync/${syncConfigId}/unpause`,
        });
    }

    whitelistIps (advertiserId, params) {
        return this.client.request({
            method: 'get',
            path: `/upyield/v1/advertisers/${advertiserId}/whitelist-ips`,
            params,
        });
    }

    createWhitelistIps (advertiserId, payload) {
        return this.client.request({
            method: 'post',
            path: `/upyield/v1/advertisers/${advertiserId}/whitelist-ips`,
            data: payload,
        });
    }

    updateWhitelistIps (advertiserId, whitelistId, payload) {
        return this.client.request({
            method: 'put',
            path: `/upyield/v1/advertisers/${advertiserId}/whitelist-ips/${whitelistId}`,
            data: payload,
        });
    }

    updateParametersGenerator (advertiserId, payload) {
        return this.client.request({
            method: 'put',
            path: `/upyield/v1/advertisers/${advertiserId}/update-or-create-parameter-generator`,
            data: payload,
        });
    }

    deleteWhitelistIps (advertiserId, whitelistId) {
        return this.client.request({
            method: 'delete',
            path: `/upyield/v1/advertisers/${advertiserId}/whitelist-ips/${whitelistId}`,
        });
    }

    getCapsParamsMapping (advertiserId, params) {
        return this.client.request({
            method: 'get',
            path: `/upyield/v1/advertisers/${advertiserId}/caps-params-mapping`,
            params,
        });
    }

    createCapsParamsMapping (advertiserId, data) {
        return this.client.request({
            method: 'post',
            path: `/upyield/v1/advertisers/${advertiserId}/caps-params-mapping`,
            data,
        });
    }

    updateCapsParamsMapping (advertiserId, mappingId, data) {
        return this.client.request({
            method: 'put',
            path: `/upyield/v1/advertisers/${advertiserId}/caps-params-mapping/${mappingId}`,
            data,
        });
    }

    getCapsParamsMappingById (advertiserId, mappingId) {
        return this.client.request({
            method: 'get',
            path: `/upyield/v1/advertisers/${advertiserId}/caps-params-mapping/${mappingId}`,
        });
    }

    deleteCapsParamsMapping (advertiserId, mappingId) {
        return this.client.request({
            method: 'delete',
            path: `/upyield/v1/advertisers/${advertiserId}/caps-params-mapping/${mappingId}`,
        });
    }

    enableCapsWithParams (advertiserId) {
        return this.client.request({
            method: 'put',
            path: `/upyield/v1/advertisers/${advertiserId}/caps-with-params-enable`,
        });
    }

    disableCapsWithParams (advertiserId) {
        return this.client.request({
            method: 'put',
            path: `/upyield/v1/advertisers/${advertiserId}/caps-with-params-disable`,
        });
    }
}

export default new AdvertisersResource;
